import {
  IsArray,
  IsBoolean,
  IsDefined, IsIn,
  IsInt, IsISO8601, IsNumber,
  IsOptional,
  IsString,
  Length,
  Matches, Validate,
  ValidateNested, ValidatorConstraint, ValidatorConstraintInterface
} from 'class-validator';
import { Type } from 'class-transformer';
import { validationPatterns } from '../../constants';

@ValidatorConstraint({ name: 'IsWeekdayValid' })
export class IsWeekdayValidConstraint implements ValidatorConstraintInterface {
  validate(weekday: number): boolean {
    return Number.isInteger(weekday) && weekday >= 0 && weekday <= 6;
  }

  defaultMessage() {
    return 'Weekday must be an integer between 0 and 6';
  }
}

export class SmartMenuUploadDto {
  @IsOptional()
  @IsInt()
  id?: number;

  @IsDefined()
  @IsString()
  @Length(1, 255)
  name: string;

  @IsDefined()
  @IsInt()
  merchantId: number;

  @IsDefined()
  @IsInt()
  saleMediumId: number;

  @IsOptional()
  @IsInt()
  priceBracketId?: number;

  @IsDefined()
  @IsInt()
  brandId: number;

  @IsDefined()
  @IsInt()
  merchantCurrencyId: number;

  @IsOptional()
  @IsBoolean()
  isKiosk?: boolean;

  @IsOptional()
  @IsBoolean()
  isPOS?: boolean;

  @IsOptional()
  @IsBoolean()
  isParked?: boolean;

  @IsOptional()
  @IsBoolean()
  isOrderAtTable?: boolean;

  @IsOptional()
  @IsBoolean()
  isDeliveryPartner?: boolean;

  @IsOptional()
  @IsBoolean()
  isUltraOrder?: boolean;

  @IsOptional()
  @IsBoolean()
  isWebOrdering?: boolean;

  @IsOptional()
  @IsBoolean()
  isMobile?: boolean;

  @IsOptional()
  @IsBoolean()
  posProductLookup?: boolean;

  @IsOptional()
  @IsBoolean()
  isUrlAccessible?: boolean;

  @IsOptional()
  @IsBoolean()
  online?: boolean;

  @IsOptional()
  @IsArray()
  @IsString({ each: true })
  tags?: string[];

  @IsDefined()
  @IsInt()
  sequence: number;

  @IsDefined()
  @IsString()
  @IsISO8601()
  @Matches(validationPatterns.day)
  startDate: string;

  @IsOptional()
  @IsString()
  @IsISO8601()
  @Matches(validationPatterns.day)
  endDate?: string;

  @IsDefined()
  @ValidateNested({ each: true })
  @Type(() => SmartMenuUploadMenuCategory)
  menuCategories: SmartMenuUploadMenuCategory[];

  @IsOptional()
  @ValidateNested({ each: true })
  @Type(() => SmartMenuUploadMenuHours)
  menuHours?: SmartMenuUploadMenuHours[];
}

export class SmartMenuUploadMenuHours {
  @IsDefined()
  @IsInt()
  @Validate(IsWeekdayValidConstraint)
  weekday: number;

  @IsDefined()
  @IsString()
  @Matches(/^([0-9]{2}):([0-9]{2}):([0-9]{2})$/, {
    message: 'startTime must be in HH:MM:SS format'
  })
  startTime: string;

  @IsString()
  @Matches(/^([0-9]{2}):([0-9]{2}):([0-9]{2})$/, {
    message: 'endTime must be in HH:MM:SS format'
  })
  @IsDefined()
  endTime: string;
}

export class SmartMenuUploadMenuCategory {
  @IsOptional()
  @IsInt()
  id?: number;

  @IsDefined()
  @IsString()
  @Length(1, 100)
  name: string;

  @IsOptional()
  @IsString()
  @Length(0, 255)
  description?: string;

  @IsOptional()
  @IsArray()
  @IsString({ each: true })
  tags?: string[];

  @IsDefined()
  @ValidateNested({ each: true })
  @Type(() => SmartMenuUploadProduct)
  products: SmartMenuUploadProduct[];
}

export class SmartMenuUploadProductCategory {
  @IsOptional()
  @IsInt()
  id?: number;

  @IsOptional()
  @IsString()
  @Matches(validationPatterns.categoryCode)
  code?: string;

  @IsDefined()
  @IsString()
  @Length(1, 255)
  name: string;
}

export class SmartMenuUploadAllergen {
  @IsOptional()
  @IsInt()
  id?: number;

  @IsDefined()
  @IsString()
  @Length(1, 255)
  name: string;
}

export class SmartMenuUploadModifierGroup {
  @IsOptional()
  @IsInt()
  id?: number;

  @IsDefined()
  @IsString()
  @Length(1, 255)
  name: string;

  @IsDefined()
  @IsInt()
  minRequired: number;

  @IsDefined()
  @IsInt()
  maxAllowed: number;

  @IsOptional()
  @IsArray()
  @IsString({ each: true })
  tags?: string[];
}

export class SmartMenuUploadModifier {
  @IsOptional()
  @IsInt()
  id?: number;

  @IsDefined()
  @IsString()
  @Length(1, 255)
  name: string;

  @IsOptional()
  @IsString()
  @Length(0, 255)
  description?: string;

  @IsDefined()
  @IsNumber()
  @Type(() => Number)
  price: number;

  @IsDefined()
  @ValidateNested()
  @Type(() => SmartMenuUploadProductCategory)
  productCategory: SmartMenuUploadProductCategory;

  @IsDefined()
  @ValidateNested()
  @Type(() => SmartMenuUploadProductCategory)
  productCategoryLev2: SmartMenuUploadProductCategory;

  @IsDefined()
  @ValidateNested()
  @Type(() => SmartMenuUploadProductCategory)
  productCategoryLev1: SmartMenuUploadProductCategory;

  @IsDefined()
  @ValidateNested()
  @Type(() => SmartMenuUploadModifierGroup)
  modifierGroup: SmartMenuUploadModifierGroup;

  @IsOptional()
  @ValidateNested({ each: true })
  @Type(() => SmartMenuUploadAllergen)
  allergens?: SmartMenuUploadAllergen[];

  @IsOptional()
  @IsArray()
  @IsString({ each: true })
  tags?: string[];
}

export class SmartMenuUploadProduct {
  @IsOptional()
  @IsInt()
  id?: number;

  @IsOptional()
  @IsInt()
  menuCategoryId?: number;

  @IsDefined()
  @IsString()
  @Length(1, 255)
  name: string;

  @IsOptional()
  @IsString()
  @Length(0, 255)
  description?: string;

  @IsDefined()
  @IsNumber()
  @Type(() => Number)
  price: number;

  @IsDefined()
  @IsInt()
  vatRateId: number;

  @IsDefined()
  @ValidateNested()
  @Type(() => SmartMenuUploadProductCategory)
  productCategory: SmartMenuUploadProductCategory;

  @IsDefined()
  @ValidateNested()
  @Type(() => SmartMenuUploadProductCategory)
  productCategoryLev2: SmartMenuUploadProductCategory;

  @IsDefined()
  @ValidateNested()
  @Type(() => SmartMenuUploadProductCategory)
  productCategoryLev1: SmartMenuUploadProductCategory;

  @IsOptional()
  @ValidateNested({ each: true })
  @Type(() => SmartMenuUploadAllergen)
  allergens?: SmartMenuUploadAllergen[];

  @IsOptional()
  @ValidateNested({ each: true })
  @Type(() => SmartMenuUploadModifier)
  modifiers?: SmartMenuUploadModifier[];

  @IsOptional()
  @IsArray()
  @IsString({ each: true })
  tags?: string[];
}